import { ReactNode, PureComponent, Component, ReactElement } from 'react';
import _ from 'lodash';
import * as CanvasGauges from "canvas-gauges";
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { validateHighlightsWithMsg } from './GaugesHelper';
import { BoardTheming } from '@UI/Dashboard/BoardTheming';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { GaugeHelperHighlight, SettingsType } from '@Primitive/SettingsListType';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { isDefined, IsNullOrEmpty, isNumeric } from '@Utils/Micro';
import { SettingsList } from '@Primitive/SettingsList';
import { Plegma } from '@API/Api';
import { WebThing, WebThingPort } from '@API/WebThing';
import { WebThingPortType } from '@API/WebNodeTypes';
import { IconURIPath } from '../WidgetsThingsStructs';
import type { LocalizationMap } from '@Primitive/Localization/Localization';
// =======================================================================================================

export interface RadialGaugeData {
    RadialGaugeSettings: RadialGaugeSettings;
}

export interface RadialGaugeSettings {
    Units: string;
    MinValue: number;
    MaxValue: number;
    MajorTicksPeriod: number;
    MajorTicks: string[];
    MinorTicks: number;
    ValueBox: boolean;
    DecimalPlaces: number;
    enableDecimal: boolean;
    Highlights: GaugeHelperHighlight[];
    SubTitle: string;
    reverseMode: boolean;
    AutoAdjustBounds?: boolean;

    theme: {
        // All Colors
        colorPlate?: string,
        colorPlateEnd?: string,
        colorMajorTicks?: string,
        colorMinorTicks?: string,
        colorStrokeTicks?: string,
        colorTitle?: string,
        colorUnits?: string,
        colorNumbers?: string,
        colorNeedle?: string,
        colorNeedleEnd?: string,
        colorValueText?: string,
        colorValueTextShadow?: string,
        colorBorderShadow?: string,
        colorBorderOuter?: string,
        colorBorderOuterEnd?: string,
        colorBorderMiddle?: string,
        colorBorderMiddleEnd?: string,
        colorBorderInner?: string,
        colorBorderInnerEnd?: string,
        colorValueBoxRect?: string,
        colorValueBoxRectEnd?: string,
        colorValueBoxBackground?: string,
        colorValueBoxShadow?: string,
        colorNeedleShadowUp?: string,
        colorNeedleShadowDown?: string,
        colorBarStroke?: string,
        colorBar?: string,
        colorBarProgress?: string,
        colorBarShadow?: string,

        // Needle
        needleShadow?: boolean,
        needleType?: string,
        needleStart?: number,
        needleEnd?: number,
        needleWidth?: number,

        //font-style
        fontTitleStyle?: CanvasGauges.FontStyle,
        fontUnitsStyle?: CanvasGauges.FontStyle,
        fontNumbersStyle?: CanvasGauges.FontStyle,

        //font-weight
        fontTitleWeight?: CanvasGauges.FontWeight,
        fontUnitsWeight?: CanvasGauges.FontWeight,
        fontNumbersWeight?: CanvasGauges.FontWeight,

        //font-size
        fontTitleSize?: number,
        fontUnitsSize?: number,
        fontNumbersSize?: number
    }
}
// ------------------------------------------------------------------------------------------------------
class RadialGauge extends PureComponent<WidgetsThingsStructs.PartialIWidgetThingsProps<RadialGaugeData>> {
    private _element: HTMLElement;
    private _container: CanvasGauges.RadialGauge;
    private _value: number;
    // ---------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<RadialGaugeData>) {
        super(props);
        // initialize to minimum value
        this._value = this.props.WidgetData.RadialGaugeSettings.MinValue;
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        return (
            <div style={{
                width: this.props.width,
                height: this.props.height,
                overflow: 'hidden',
                padding: WidgetsThingsStructs.defaultWidgetContainerPadding
            }}>
                <canvas
                    ref={(div) => { this._element = div; }}
                    data-type="radial-gauge">
                </canvas>
            </div>
        );
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        this.initRadialGauge(this.props);
    }
    // ---------------------------------------------------------------------------------------------------
    componentWillUnmount() {
        if (this._container != null)
            this._container.destroy();
    }
    // ---------------------------------------------------------------------------------------------------
    updateDataFromPort(props: WidgetsThingsStructs.PartialIWidgetThingsProps<RadialGaugeData>): boolean {
        if (_.has(props.PortsStates, PortKeys.Data)) {
            let port = props.PortsStates[PortKeys.Data];

            if (!isDefined(port.State) || port.State.length == 0) {
                if (this._container != null) {
                    this._container.value = this._value;
                    this._container.options.valueText = "n/a";
                    if (this._container.canvas != null)
                        this._container.draw();
                    return false;
                } else
                    return true;
            }

            if (isNumeric(port.State)) {
                this._value = parseFloat(port.State);
                if (this._container != null) {
                    // update options
                    this._container.update(this._getOptions(props));
                    if (this._container.canvas != null)
                        this._container.draw();
                    return false;
                } else
                    return true;
            }
        }
        return false;
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<RadialGaugeData>) {
        let needUpdate = false

        if (this.props.PortVersion != prevProps.PortVersion)
            needUpdate = this.updateDataFromPort(this.props);

        if (needUpdate ||
            this.props.width != prevProps.width ||
            this.props.height != prevProps.height ||
            !_.isEqual(this.props.WidgetData, prevProps.WidgetData) ||
            !_.isEqual(this.props.LocalizationMap, prevProps.LocalizationMap)
        ) {
            needUpdate = true;
        }

        if (needUpdate)
            this._container.update(this._getOptions(this.props));
    }
    // ---------------------------------------------------------------------------------------------------
    initRadialGauge(props: WidgetsThingsStructs.PartialIWidgetThingsProps<RadialGaugeData>) {
        if (this._container != null) {
            this.updateDataFromPort(props);
            this._container.update(this._getOptions(props));
        } else {
            this.updateDataFromPort(props);
            this._container = new CanvasGauges.RadialGauge(this._getOptions(props)).draw();

            // WORKAROUND: Update the value and after the first drawing
            // it seem the library have a timing issue
            setTimeout(() => {
                this.updateDataFromPort(props);
            }, 30);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _getValue(props: WidgetsThingsStructs.PartialIWidgetThingsProps<RadialGaugeData>): { value: number, valueText: string } {
        const settings = props.WidgetData.RadialGaugeSettings;
        const value = settings.reverseMode ?
            (settings.MaxValue - this._value + settings.MinValue)
            : this._value

        // Check that we have valid value
        if (!_.isNumber(this._value)) {
            return {
                value,
                valueText: ""
            }
        }

        // return the value to be displayd in gauge
        return {
            value: value,
            valueText: this._value.toFixed(settings.DecimalPlaces)
        };
    }
    // ---------------------------------------------------------------------------------------------------
    private _getOptions(props: WidgetsThingsStructs.PartialIWidgetThingsProps<RadialGaugeData>): CanvasGauges.RadialGaugeOptions {
        const settings = props.WidgetData.RadialGaugeSettings;

        let title = props.LocalizationMap['SubTitle'];
        if (IsNullOrEmpty(title))
            title = settings.SubTitle;

        let units = props.LocalizationMap['Units'];
        if (IsNullOrEmpty(title))
            units = settings.Units;

        let _majorTicks = settings.MajorTicks;
        let _highlights = settings.Highlights;
        let _from: number;
        let _to: number;

        const { value, valueText } = this._getValue(props);

        if (settings.AutoAdjustBounds) {
            let CurrentValue = value;

            if (settings.reverseMode) {
                CurrentValue = this._value;
            }
            //We use abs in order to cover also reverse mode
            if (CurrentValue > settings.MaxValue) {
                let newMaxValue = CurrentValue;

                //Find the first value from which max-min/major ticks => integer
                let x = (CurrentValue - settings.MinValue) / settings.MajorTicksPeriod

                if (!Number.isInteger(x)) {
                    newMaxValue = Math.ceil(x) * settings.MajorTicksPeriod - settings.MinValue;
                }

                let prevsMaxValue = _majorTicks.slice(-1)[0];
                //Find Firtst tick after limit update
                let currentMajorTick: number = parseInt(prevsMaxValue) + settings.MajorTicksPeriod;

                //Add the extra ticks due to the limit update
                while (currentMajorTick < newMaxValue) {
                    _majorTicks.push(currentMajorTick.toString());
                    currentMajorTick = currentMajorTick + settings.MajorTicksPeriod;
                }
                _majorTicks.push(newMaxValue.toString());

                //Update max value
                settings.MaxValue = newMaxValue;
            }
        }

        //check if in counter-clockwise mode
        if (settings.reverseMode) {
            //reverse Ticks so that in UI the last value of the gauge becomes the first
            _majorTicks = _.reverse(_.clone(_majorTicks));
            _highlights = _.cloneDeep(settings.Highlights);
            _highlights.forEach((_highlight) => {

                _from = settings.MaxValue - _highlight.to + settings.MinValue;
                _to = settings.MaxValue - _highlight.from + settings.MinValue;

                _highlight.from = _from;
                _highlight.to = _to;
            });
        }

        // Get the theme from the board
        let boardtheme = BoardTheming.GetActiveTheme();
        let theme = _.merge({}, boardtheme.widgets.radialGauge, settings.theme);

        return {
            width: props.width,
            height: props.height,
            renderTo: this._element,
            value: value,
            units: units,
            minValue: settings.MinValue,
            maxValue: settings.MaxValue,
            majorTicks: _majorTicks,
            minorTicks: settings.MinorTicks,
            valueBox: settings.ValueBox,
            valueText: valueText,
            valueInt: 1,
            valueDec: settings.DecimalPlaces,
            highlights: _highlights,
            title: title,
            strokeTicks: true,
            colorPlate: 'transparent',
            borderShadowWidth: 0,
            borders: false,
            needleCircleSize: 7,
            needleCircleOuter: true,
            needleCircleInner: false,
            animationDuration: 600,
            animationRule: "dequad",
            ...theme
        };
    }
    // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
interface RadialGaugeWidgetSettingsState {
    widgetData: RadialGaugeData;
}

class RadialGaugeWidgetSettings extends Component<IWidgetSettingsProps<RadialGaugeData>, RadialGaugeWidgetSettingsState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<RadialGaugeData>) {
        super(props);
        this.state = { widgetData: _.cloneDeep(this.props.WidgetData) };
        this.saveData = this.saveData.bind(this);
    }
    // ---------------------------------------------------------------------------------------------------
    saveData() {
        if (isDefined(this.props.onWidgetDataUpdate)) {
            const settings = this.state.widgetData.RadialGaugeSettings;

            let majorTicks: string[] = [settings.MinValue.toString()];
            let i: number = settings.MinValue + settings.MajorTicksPeriod;

            while (i < settings.MaxValue) {
                majorTicks.push(i.toString());
                i = i + settings.MajorTicksPeriod;
            }

            majorTicks.push(settings.MaxValue.toString());

            this.setState((s: RadialGaugeWidgetSettingsState) => {
                s.widgetData.RadialGaugeSettings.MajorTicks = majorTicks;
                this.props.onWidgetDataUpdate(s.widgetData);
                return s;
            });
        }
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        const colorsProps = [
            { dataPath: 'RadialGaugeSettings.theme.colorPlate', label: 'Plate' },
            { dataPath: 'RadialGaugeSettings.theme.colorPlateEnd', label: 'Plate End' },
            { dataPath: 'RadialGaugeSettings.theme.colorMajorTicks', label: 'Major Ticks' },
            { dataPath: 'RadialGaugeSettings.theme.colorMinorTicks', label: 'Minor Ticks' },
            { dataPath: 'RadialGaugeSettings.theme.colorTitle', label: 'Title' },
            { dataPath: 'RadialGaugeSettings.theme.colorUnits', label: 'Units' },
            { dataPath: 'RadialGaugeSettings.theme.colorNumbers', label: 'Numbers' },
            { dataPath: 'RadialGaugeSettings.theme.colorNeedle', label: 'Needle' },
            { dataPath: 'RadialGaugeSettings.theme.colorNeedleEnd', label: 'Needle End' },
            { dataPath: 'RadialGaugeSettings.theme.colorValueText', label: 'Value Text' },
            { dataPath: 'RadialGaugeSettings.theme.colorValueTextShadow', label: 'Value TextShadow' },
            { dataPath: 'RadialGaugeSettings.theme.colorBorderShadow', label: 'Border Shadow' },
            { dataPath: 'RadialGaugeSettings.theme.colorBorderOuter', label: 'Border Outer' },
            { dataPath: 'RadialGaugeSettings.theme.colorBorderOuterEnd', label: 'Border OuterEnd' },
            { dataPath: 'RadialGaugeSettings.theme.colorBorderMiddle', label: 'Border Middle' },
            { dataPath: 'RadialGaugeSettings.theme.colorBorderMiddleEnd', label: 'Border Middle End' },
            { dataPath: 'RadialGaugeSettings.theme.colorBorderInner', label: 'Border Inner' },
            { dataPath: 'RadialGaugeSettings.theme.colorBorderInnerEnd', label: 'Border Inner End' },
            { dataPath: 'RadialGaugeSettings.theme.colorValueBoxRect', label: 'Value BoxRect' },
            { dataPath: 'RadialGaugeSettings.theme.colorValueBoxRectEnd', label: 'Value BoxRect End' },
            { dataPath: 'RadialGaugeSettings.theme.colorValueBoxBackground', label: 'Value Box Background' },
            { dataPath: 'RadialGaugeSettings.theme.colorValueBoxShadow', label: 'Value Box Shadow' },
            { dataPath: 'RadialGaugeSettings.theme.colorNeedleShadowUp', label: 'Needle ShadowUp' },
            { dataPath: 'RadialGaugeSettings.theme.colorNeedleShadowDown', label: 'Needle ShadowDown' },
            { dataPath: 'RadialGaugeSettings.theme.colorBarStroke', label: 'Bar Stroke' },
            { dataPath: 'RadialGaugeSettings.theme.colorBar', label: 'Bar' },
            { dataPath: 'RadialGaugeSettings.theme.colorBarProgress', label: 'Bar Progress' },
            { dataPath: 'RadialGaugeSettings.theme.colorBarShadow', label: 'Bar Shadow' },
        ];

        return <SettingsList
            AutoSave={true}
            Data={this.state.widgetData}
            SettingsMap={[
                {
                    name: '',
                    elements: [
                        {
                            type: SettingsType.String,
                            label: 'Title',
                            dataPath: 'RadialGaugeSettings.SubTitle',
                            tooltip: 'Title to be displayed in the center of the gauge.'
                        },
                        {
                            type: SettingsType.String,
                            label: 'Units',
                            dataPath: 'RadialGaugeSettings.Units',
                            oneLine: true,
                            tooltip: 'Units symbol to be shown.'
                        },
                        {
                            type: SettingsType.Number,
                            label: 'Min Value',
                            dataPath: 'RadialGaugeSettings.MinValue',
                            oneLine: true,
                            isDecimal: true,
                            tooltip: 'Minimum value of gauge.'
                        },
                        {
                            type: SettingsType.Number,
                            label: 'Max Value',
                            dataPath: 'RadialGaugeSettings.MaxValue',
                            oneLine: true,
                            isDecimal: true,
                            tooltip: 'Maximum value of gauge.'
                        },
                        {
                            type: SettingsType.Number,
                            label: 'Major Ticks Period',
                            dataPath: 'RadialGaugeSettings.MajorTicksPeriod',
                            oneLine: true,
                            minValue: 0,
                            tooltip: 'Set range of the displayed values e.g. (For values 0-100, with major ticks period set 10, values will be displayed 0,10,20,...,100).'
                        },
                        {
                            type: SettingsType.Number,
                            label: 'Minor Ticks',
                            dataPath: 'RadialGaugeSettings.MinorTicks',
                            oneLine: true,
                            minValue: 0,
                            tooltip: 'Set the number of ticks between 2 values of the gauge.'
                        },
                        {
                            type: SettingsType.Boolean,
                            label: 'Value Box',
                            dataPath: 'RadialGaugeSettings.ValueBox',
                            tooltip: '	Whether to show a box with the gauge value or not.'
                        },
                        {
                            type: SettingsType.Number,
                            label: 'Decimal Places',
                            isHidden: (data: RadialGaugeData) => !data.RadialGaugeSettings.ValueBox,
                            dataPath: 'RadialGaugeSettings.DecimalPlaces',
                            minValue: 0,
                            maxValue: 20,
                            oneLine: true,
                            tooltip: 'The number of digits after the decimal point.'
                        },
                        {
                            type: SettingsType.Boolean,
                            label: 'Decimal Highlights',
                            dataPath: 'RadialGaugeSettings.enableDecimal',
                            tooltip: 'Whether to enable decimal values in from and to values of highlights.'
                        },
                        {
                            type: SettingsType.Highlights,
                            label: 'Highlights',
                            dataPath: 'RadialGaugeSettings.Highlights',
                            tooltip: "Color to use based on gauge's value range.",
                            enableDecimal: 'RadialGaugeSettings.enableDecimal',
                            minValue: 'RadialGaugeSettings.MinValue',
                            maxValue: 'RadialGaugeSettings.MaxValue',
                        },
                        {
                            type: SettingsType.Boolean,
                            label: 'Counter-Clockwise',
                            dataPath: 'RadialGaugeSettings.reverseMode',
                            tooltip: 'Whether to start counting from the left side and behave like a counter-clockwise gauge.',
                        },
                        {
                            type: SettingsType.Boolean,
                            label: 'Auto Adjust Bounds',
                            dataPath: 'RadialGaugeSettings.AutoAdjustBounds',
                            tooltip: 'Enable auto limits mode.',
                        }
                    ]
                },
                {
                    name: 'Theme',
                    elements: _.flatMap([
                        _.map(colorsProps, c => {
                            return {
                                type: SettingsType.ColorPicker,
                                label: c.label,
                                dataPath: c.dataPath,
                                isNullable: true
                            }
                        }),
                        [
                            {
                                type: SettingsType.Boolean,
                                label: 'Needle Shadow',
                                dataPath: 'RadialGaugeSettings.theme.needleShadow',
                                isNullable: true
                            },
                            {
                                type: SettingsType.DropDown,
                                label: 'Needle Type',
                                dataPath: 'RadialGaugeSettings.theme.needleType',
                                isNullable: true,
                                items: [
                                    { label: "Arrow", value: "arrow" },
                                    { label: "Line", value: "line" },
                                ]
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Needle Start',
                                dataPath: 'RadialGaugeSettings.theme.needleStart',
                                minValue: 0,
                                maxValue: 100,
                                stepValue: 1,
                                valuePosfix: "%",
                                isNullable: true
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Needle End',
                                dataPath: 'RadialGaugeSettings.theme.needleEnd',
                                minValue: 0,
                                maxValue: 100,
                                stepValue: 1,
                                valuePosfix: "%",
                                isNullable: true
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Needle Width',
                                dataPath: 'RadialGaugeSettings.theme.needleWidth',
                                minValue: 2,
                                maxValue: 20,
                                stepValue: 1,
                                valuePosfix: "px",
                                isNullable: true
                            },
                            {
                                type: SettingsType.DropDown,
                                label: 'Title Font Style',
                                dataPath: 'RadialGaugeSettings.theme.fontTitleStyle',
                                isNullable: true,
                                items: [
                                    { label: "Normal", value: "Normal" },
                                    { label: "Italic", value: "Italic" },
                                    { label: "Oblique", value: "Oblique" },

                                ]
                            },
                            {
                                type: SettingsType.DropDown,
                                label: 'Numbers Font Weight',
                                dataPath: 'RadialGaugeSettings.theme.fontTitleWeight',
                                isNullable: true,
                                items: [
                                    { label: "Normal", value: "Normal" },
                                    { label: "Bold", value: "bold" },
                                    { label: "Bolder", value: "bolder" },
                                    { label: "Lighter", value: "lighter" },
                                    { label: "100", value: "100" },
                                    { label: "200", value: "200" },
                                    { label: "300", value: "300" },
                                    { label: "400", value: "400" },
                                    { label: "500", value: "500" },
                                    { label: "600", value: "600" },
                                    { label: "700", value: "700" },
                                    { label: "800", value: "800" },
                                    { label: "900", value: "900" },
                                ]
                            },
                            {
                                type: SettingsType.Number,
                                label: 'Title Font Size',
                                dataPath: 'RadialGaugeSettings.theme.fontTitleSize',
                                oneLine: true,
                                isDecimal: true,
                                minValue: 5
                            },
                            {
                                type: SettingsType.DropDown,
                                label: 'Units Font Style',
                                dataPath: 'RadialGaugeSettings.theme.fontUnitsStyle',
                                isNullable: true,
                                items: [
                                    { label: "Normal", value: "Normal" },
                                    { label: "Italic", value: "Italic" },
                                    { label: "Oblique", value: "Oblique" },

                                ]
                            },
                            {
                                type: SettingsType.DropDown,
                                label: 'Units Font Weight',
                                dataPath: 'RadialGaugeSettings.theme.fontUnitsWeight',
                                isNullable: true,
                                items: [
                                    { label: "Normal", value: "Normal" },
                                    { label: "Bold", value: "bold" },
                                    { label: "Bolder", value: "bolder" },
                                    { label: "Lighter", value: "lighter" },
                                    { label: "100", value: "100" },
                                    { label: "200", value: "200" },
                                    { label: "300", value: "300" },
                                    { label: "400", value: "400" },
                                    { label: "500", value: "500" },
                                    { label: "600", value: "600" },
                                    { label: "700", value: "700" },
                                    { label: "800", value: "800" },
                                    { label: "900", value: "900" },
                                ]
                            },
                            {
                                type: SettingsType.Number,
                                label: 'Units Font Size',
                                dataPath: 'RadialGaugeSettings.theme.fontUnitsSize',
                                oneLine: true,
                                isDecimal: true,
                                minValue: 5
                            },
                            {
                                type: SettingsType.DropDown,
                                label: 'Numbers Font Style',
                                dataPath: 'RadialGaugeSettings.theme.fontNumbersStyle',
                                isNullable: true,
                                items: [
                                    { label: "Normal", value: "Normal" },
                                    { label: "Italic", value: "Italic" },
                                    { label: "Oblique", value: "Oblique" },

                                ]
                            },
                            {
                                type: SettingsType.DropDown,
                                label: 'Numbers Font Style',
                                dataPath: 'RadialGaugeSettings.theme.fontNumbersWeight',
                                isNullable: true,
                                items: [
                                    { label: "Normal", value: "Normal" },
                                    { label: "Bold", value: "bold" },
                                    { label: "Bolder", value: "bolder" },
                                    { label: "Lighter", value: "lighter" },
                                    { label: "100", value: "100" },
                                    { label: "200", value: "200" },
                                    { label: "300", value: "300" },
                                    { label: "400", value: "400" },
                                    { label: "500", value: "500" },
                                    { label: "600", value: "600" },
                                    { label: "700", value: "700" },
                                    { label: "800", value: "800" },
                                    { label: "900", value: "900" },
                                ]
                            },
                            {
                                type: SettingsType.Number,
                                label: 'Numbers Font Size',
                                dataPath: 'RadialGaugeSettings.theme.fontNumbersSize',
                                oneLine: true,
                                isDecimal: true,
                                minValue: 5
                            }
                        ]
                    ],
                    )
                }
            ]}
            onCheckData={(data) => {
                const settings = data.RadialGaugeSettings;
                // todo: use Micro.ts function (if we don't have this kind of helper function, export to Micro.ts)
                const isNumber = (v) => {
                    return !isNaN(parseFloat(v)) && isFinite(v);
                };
                const periodsNum = (settings.MaxValue - settings.MinValue)
                    / settings.MajorTicksPeriod;

                let isValid: boolean = true;
                let errorMsg: string = '';

                if (!isNumber(settings.MinValue)) {
                    errorMsg = 'Min Value should be numeric';
                    isValid = false;
                }
                else if (!isNumber(settings.MaxValue)) {
                    errorMsg = 'Max Value should be numeric';
                    isValid = false;
                }
                else if (!isNumber(settings.MajorTicksPeriod)) {
                    errorMsg = 'Major Ticks Period should be numeric';
                    isValid = false;
                }
                else if (!isNumber(settings.MinorTicks)) {
                    errorMsg = 'Minor Ticks should be numeric';
                    isValid = false;
                }
                else if (settings.MinValue > settings.MaxValue) {
                    errorMsg = 'Max Value should be greater than Min Value';
                    isValid = false;
                }
                else if (periodsNum !== parseInt(periodsNum.toString())) {
                    errorMsg = 'Major Ticks Period must be the integer divider \
                    of the difference between Max and Min values';
                    isValid = false;
                }
                else {
                    const highlightsRes = validateHighlightsWithMsg(settings.MinValue, settings.MaxValue, settings.Highlights);
                    if (!highlightsRes.valid)
                        return highlightsRes;
                }

                return {
                    valid: isValid,
                    errorMsg: errorMsg
                };
            }}
            onSaveData={(data) => {
                this.setState(
                    { widgetData: data },
                    () => this.saveData());
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================
// Gauge Description interface
const enum PortKeys {
    Data = "Data"
}
export class RadialGaugeWidget extends IWidgetThingsClass<RadialGaugeData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): ReactNode {
        return <RadialGauge />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <RadialGaugeWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];
        ports.push(new WebThingPort(
            {
                Name: "Data",
                PortKey: PortKeys.Data,
                Type: Plegma.ePortType.Decimal,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ""
            },
            WebThingPortType.Data));
        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): RadialGaugeData {
        return {
            RadialGaugeSettings: {
                Units: "%",
                MinValue: 0,
                MaxValue: 100,
                ValueBox: true,
                DecimalPlaces: 2,
                MajorTicksPeriod: 10,
                MajorTicks: [],
                MinorTicks: 2,
                enableDecimal: false,
                Highlights: [],
                SubTitle: "",
                reverseMode: false,
                AutoAdjustBounds: false,
                theme: {}
            }
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() {
        return RadialGaugeWidget.GetDefaultWidgetDataStatic();
    }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: RadialGaugeData = RadialGaugeWidget.GetDefaultWidgetDataStatic();

        // fill sample ticks
        data.RadialGaugeSettings.MajorTicks = ["0", "10", "20", "30", "40", "50", "60", "70", "80", "90", "100"];

        // fill sample hightlights
        data.RadialGaugeSettings.Highlights = [{
            "from": 80,
            "to": 100,
            "color": "rgba(200,50,50,.75)"
        }];

        return {
            Title: "Radial Gauge",
            WidgetTitle: "",
            Key: "RadialGaugeWidget",
            Type: "RadialGaugeWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: IconURIPath + "/svg/radial_gauge.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        const res = {};

        // expose title (if any)
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        // expose `SubTitle` (if any)
        if (!IsNullOrEmpty(this.WidgetData.RadialGaugeSettings.SubTitle))
            res['SubTitle'] = this.WidgetData.RadialGaugeSettings.SubTitle;

        // expose `Units` (if any)
        if (!IsNullOrEmpty(this.WidgetData.RadialGaugeSettings.Units))
            res['Units'] = this.WidgetData.RadialGaugeSettings.Units;

        return res;
    };
    // ------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return PortKeys.Data; };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================